import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../api';
import { showError, showSuccess } from '../utils/errorHandler';

const inputStyle = {
  width: '100%',
  padding: '0.85rem 1rem',
  background: 'rgba(255,255,255,0.06)',
  border: '1.5px solid rgba(255,255,255,0.15)',
  borderRadius: '10px',
  color: 'white',
  fontSize: '0.95rem',
  outline: 'none',
  transition: 'border-color 0.2s ease',
  boxSizing: 'border-box',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none'
};

const selectStyle = {
  ...inputStyle,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 1rem center',
  paddingRight: '2.5rem',
  cursor: 'pointer'
};

function CompleteProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    doc_type: 'DNI',
    doc_number: '',
    address_full: '',
    country: 'España',
    iban: '',
    phone: ''
  });

  useEffect(() => {
    checkProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkProfile = async () => {
    try {
      const response = await api.get('/api/profile');
      const profileData = response.data;
      
      if (profileData.doc_number && profileData.address_full) {
        navigate('/dashboard');
        return;
      }
      
      setFormData({
        doc_type: profileData.doc_type || 'DNI',
        doc_number: profileData.doc_number || '',
        address_full: profileData.address_full || '',
        country: profileData.country || 'España',
        iban: profileData.iban || '',
        phone: profileData.phone || ''
      });
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.doc_number || !formData.address_full || !formData.phone || !formData.iban) {
      showError({ message: t('completeProfile.fillRequired') || 'Si us plau, completa tots els camps obligatoris (inclòs IBAN)' });
      return;
    }

    setSaving(true);
    try {
      await api.put('/api/profile', formData);
      showSuccess(t('completeProfile.saved') || 'Perfil completat correctament');
      setTimeout(() => navigate('/dashboard'), 1000);
    } catch (error) {
      showError(error, 'Error al guardar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #141414 100%)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '24px',
        padding: '2.5rem',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', color: 'white', marginBottom: '0.5rem' }}>
            {t('completeProfile.welcome') || 'Benvingut'}, {user?.name}!
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>
            {t('completeProfile.subtitle') || 'Completa el teu perfil per començar a invertir'}
          </p>
        </div>

        <div style={{
          background: 'rgba(59,130,246,0.1)',
          border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '2rem'
        }}>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem', margin: 0, lineHeight: 1.6 }}>
            {t('completeProfile.infoText') || 'Per complir amb la normativa legal, necessitem els teus dades personals i fiscals abans de poder processar la teva primera sol·licitud de préstec.'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Document Type & Number */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  {t('labels.docType')} *
                </label>
                <select
                  value={formData.doc_type}
                  onChange={(e) => setFormData({...formData, doc_type: e.target.value})}
                  required
                  data-testid="profile-doc-type"
                  style={selectStyle}
                >
                  <option value="DNI" style={{ background: '#1a1a1a', color: 'white' }}>DNI</option>
                  <option value="NIE" style={{ background: '#1a1a1a', color: 'white' }}>NIE</option>
                  <option value="Passport" style={{ background: '#1a1a1a', color: 'white' }}>Passaport</option>
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                  {t('labels.docNumber')} *
                </label>
                <input
                  type="text"
                  value={formData.doc_number}
                  onChange={(e) => setFormData({...formData, doc_number: e.target.value})}
                  placeholder="Ex: 12345678A"
                  required
                  data-testid="profile-doc-number"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                {t('labels.phone')} *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+34 600 000 000"
                required
                data-testid="profile-phone"
                style={inputStyle}
              />
            </div>

            {/* Address */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                {t('labels.address')} *
              </label>
              <textarea
                value={formData.address_full}
                onChange={(e) => setFormData({...formData, address_full: e.target.value})}
                placeholder={t('placeholders.address')}
                required
                rows={3}
                data-testid="profile-address"
                style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
              />
            </div>

            {/* IBAN - OBLIGATORIO */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                IBAN *
              </label>
              <input
                type="text"
                value={formData.iban}
                onChange={(e) => setFormData({...formData, iban: e.target.value.toUpperCase()})}
                placeholder="ES00 0000 0000 0000 0000 0000"
                required
                maxLength={24}
                data-testid="profile-iban"
                style={{ ...inputStyle, fontFamily: 'monospace', letterSpacing: '0.5px' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem' }}>
                {t('completeProfile.ibanNote') || 'Per rebre transferències dels interessos generats'}
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="profile-submit"
            style={{
              width: '100%',
              marginTop: '2rem',
              padding: '1rem',
              background: saving ? 'rgba(107,114,128,0.5)' : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {saving ? (t('status.saving') || 'Guardant...') : (t('completeProfile.saveButton') || 'Guardar i Continuar')}
          </button>

          <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>
            * {t('completeProfile.requiredFields') || 'Camps obligatoris'}
          </p>
        </form>
      </div>
    </div>
  );
}

export default CompleteProfile;
